import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import { api } from '@baaligames/shared-ui/lib/api';
import { motion, AnimatePresence } from 'motion/react';
import BetSlip, { BetSelection, CricketLimits } from './BetSlip';
import RulesButton from './Rules';
import { setWalletBalance, useWallet } from '@baaligames/shared-ui/lib/wallet';

// Continuous Countdown (for upcoming matches)
const CountdownTimer = ({ dateStr }: { dateStr: string }) => {
  const [timeText, setTimeText] = useState('');
  useEffect(() => {
    const updateTime = () => {
      const diff = new Date(dateStr).getTime() - new Date().getTime();
      if (diff > 0 && diff < 24 * 60 * 60 * 1000) {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const secs = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeText(`${hours > 0 ? `${hours}h ` : ''}${mins}m ${secs}s`);
      } else {
        setTimeText(`${new Date(dateStr).toLocaleDateString([], { month: 'short', day: 'numeric' })} ${new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
      }
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [dateStr]);
  return (
    <div className="flex flex-col items-center justify-center bg-blue-500/10 border border-blue-500/20 rounded-xl px-3 py-1.5 min-w-[110px]">
      <span className="text-[9px] font-bold text-blue-500 uppercase tracking-widest mb-0.5">Starts In</span>
      <span className="text-sm font-black font-mono text-blue-600 dark:text-blue-400">{timeText}</span>
    </div>
  );
};

// ── PRICE-TICK FLASH ────────────────────────────────────────────────────────────
// Whenever a displayed number changes, its element pulses yellow for a beat then
// settles back — the standard exchange "the price just moved" signal. useTick
// increments only on a REAL change (never on first render), and Flash paints an
// overlay above the wrapped element so it works over opaque buttons too.
function useTick(value: any) {
  const [tick, setTick] = useState(0);
  const prev = useRef<any>(undefined);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; prev.current = value; return; }
    if (prev.current !== value) { prev.current = value; setTick((t) => t + 1); }
  }, [value]);
  return tick;
}
function Flash({ value, className = '', rounded = 'rounded-lg', children }: { value: any; className?: string; rounded?: string; children: any }) {
  const tick = useTick(value);
  return (
    <div className={`relative ${className}`}>
      {children}
      {tick > 0 && <span key={tick} className={`pointer-events-none absolute inset-0 ${rounded} cricket-flash`} />}
    </div>
  );
}

// Parse an EntitySport score string into structured innings.
//   "410/10 (107.2 ov)"                      → [{ rw:'410/10', ov:'107.2', current:false }]
//   "140/10 (47.2 ov) & *91/3 (23.2 ov)"     → [..., { rw:'91/3', ov:'23.2', current:true }]
// The leading "*" marks the innings currently in progress.
function parseInnings(scores: string): { rw: string; ov: string; current: boolean }[] {
  if (!scores || typeof scores !== 'string') return [];
  return scores.split('&').map((raw) => {
    let part = raw.trim();
    const current = part.startsWith('*');
    if (current) part = part.replace(/^\*\s*/, '');
    const m = part.match(/^([0-9]+(?:\/[0-9]+)?(?:d|dec)?)\s*(?:\(([^)]*)\))?/i);
    const rw = m ? m[1] : part;
    const ov = m && m[2] ? m[2].replace(/ov(er)?s?/i, '').trim() : '';
    return { rw, ov, current };
  }).filter((x) => x.rw);
}

// LIVE TV — vendor stream embedded via iframe (the competitor-standard wiring).
// Tap-to-load so the heavy stream never autoplays on data connections; once
// loaded it plays with audio and supports fullscreen. Shown whenever the admin
// Live TV switch is ON; with no stream URL configured (dev stage / vendor
// outage) the player opens to an honest "can't connect right now" screen.
function LiveTv({ url, onClose }: { url: string | null; onClose: () => void }) {
  return (
    <div className="mx-1.5 mb-2 bg-black rounded-xl overflow-hidden border border-arena-200 dark:border-white/10 shadow-sm">
      {(
        <div className="relative">
          <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
            {url ? (
              <iframe
                src={url}
                title="Live TV"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                allowFullScreen
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-b from-arena-900 to-black">
                <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-white/5 border border-white/10">
                  <Icon name="signal_disconnected" size={26} className="text-white/40" />
                </span>
                <div className="font-black text-sm text-white/80">Can't connect right now</div>
                <div className="text-[11px] font-bold text-white/40 max-w-[240px] text-center">The live stream isn't available at the moment. Please try again shortly.</div>
              </div>
            )}
          </div>
          <button onClick={onClose}
            className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black/90">
            <Icon name="close" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

// Win-probability segmented bar (live only). a/draw/b are percentages (side 0/1).
// The BAR never flashes (it looked bad) — only the percentage NUMBERS tick. A gold
// trophy rides the middle of the bar, seated on the LEADING side; the trailing
// side never carries it. Hidden on a dead heat.
function WinProbBar({ winPct, teamA, teamB }: { winPct: any; teamA: string; teamB: string }) {
  const a = Math.max(0, Number(winPct?.a) || 0);
  const b = Math.max(0, Number(winPct?.b) || 0);
  const d = Math.max(0, Number(winPct?.draw) || 0);
  const total = a + b + d || 1;
  const leader = a === b ? null : (a > b ? 0 : 1);
  return (
    <div className="mx-1.5 mb-2">
      <div className="relative">
        <div className="flex h-1.5 rounded-full overflow-hidden bg-arena-100 dark:bg-arena-700">
          <div className="bg-blue-500 h-full transition-all duration-500" style={{ width: `${(a / total) * 100}%` }} />
          <div className="bg-arena-300 dark:bg-arena-500 h-full transition-all duration-500" style={{ width: `${(d / total) * 100}%` }} />
          <div className="bg-emerald-500 h-full transition-all duration-500" style={{ width: `${(b / total) * 100}%` }} />
        </div>
        {leader != null && (
          <span className={`absolute top-1/2 -translate-y-1/2 ${leader === 0 ? 'left-[calc(50%-24px)]' : 'left-[calc(50%+8px)]'} w-4 h-4 rounded-full bg-yellow-400 border border-white dark:border-arena-800 shadow-md flex items-center justify-center transition-all duration-500`}>
            <Icon name="emoji_events" size={10} className="text-black" />
          </span>
        )}
      </div>
      <div className="flex items-center justify-between mt-0.5">
        <Flash value={Math.round(a)} rounded="rounded" className="inline-flex">
          <span className="text-[10px] font-black text-blue-600 dark:text-blue-400 px-0.5">{teamA} {Math.round(a)}%</span>
        </Flash>
        {d > 0 && <span className="text-[8px] font-bold uppercase tracking-widest text-arena-400 dark:text-arena-200">draw {Math.round(d)}%</span>}
        <Flash value={Math.round(b)} rounded="rounded" className="inline-flex">
          <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 px-0.5">{Math.round(b)}% {teamB}</span>
        </Flash>
      </div>
    </div>
  );
}

// ── COMPACT-UI toolkit (V2 vertical-space redesign; V1 saved in ui-backups) ────
// The core trick: deep-detail cards ride in HORIZONTAL swipe strips instead of
// stacking vertically — every piece of information stays, styled as before, but
// the page height collapses and the markets arrive almost immediately.
function HStrip({ children }: { children: any }) {
  return (
    <div className="flex gap-2 overflow-x-auto px-3 pb-2 snap-x [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
      {children}
    </div>
  );
}
function MiniCard({ label, icon, children, w = 'min-w-[132px]' }: { label: string; icon?: string; children: any; w?: string }) {
  return (
    <div className={`${w} shrink-0 snap-start rounded-xl bg-arena-50 dark:bg-arena-900/40 border border-arena-100 dark:border-white/5 px-2 py-1.5`}>
      <div className="flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest text-arena-400 dark:text-arena-200 mb-0.5">{icon && <Icon name={icon} size={10} />}{label}</div>
      {children}
    </div>
  );
}
// Compact player card for the players strip — FULL name (wraps, never truncated),
// team flag, on-strike dot, labelled mini stat cells. Same design language, ¼ the
// vertical cost of the old stacked rows.
function PlayerMini({ name, flag, role, stats, full = false, active }: { name: string; flag?: string | null; role: string; stats: { label: string; value: any; accent?: boolean }[]; full?: boolean; active?: 'bat' | 'bowl' | null }) {
  return (
    <div className={`${full ? 'w-full' : 'w-52 shrink-0 snap-start'} rounded-xl px-1.5 py-1.5 ${active
      ? 'bg-red-50/60 dark:bg-red-500/[0.07] border border-red-300/60 dark:border-red-500/30 ring-1 ring-red-400/40'
      : 'bg-arena-50 dark:bg-arena-900/40 border border-arena-100 dark:border-white/5'}`}>
      <div className="flex items-center gap-1.5 mb-1">
        {flag && <img src={flag} alt="" className="w-4 h-4 rounded-full object-cover bg-white shrink-0" />}
        <span className="font-black text-[11px] leading-tight text-arena-900 dark:text-white">{name}</span>
        {active ? (
          <span className="ml-auto shrink-0 inline-flex items-center" title={active === 'bat' ? 'On strike' : 'Bowling'}>
            {active === 'bat' ? <BatSvg className="w-4 h-4" /> : <BallSvg className="w-3.5 h-3.5" />}
          </span>
        ) : (
          <span className="text-[7px] font-bold uppercase tracking-widest text-arena-400 dark:text-arena-200 ml-auto shrink-0">{role}</span>
        )}
      </div>
      <div className="flex items-stretch divide-x divide-arena-200/60 dark:divide-white/10 rounded-lg bg-white dark:bg-arena-800/60 py-1">
        {stats.map((s, i) => (
          <div key={i} className="flex-1 flex flex-col items-center px-0.5">
            <Flash value={s.value} rounded="rounded" className="inline-flex">
              <span className={`font-mono font-black text-[11px] leading-none px-0.5 ${s.accent ? 'text-brand-600 dark:text-brand-400' : 'text-arena-900 dark:text-white'}`}>{s.value ?? '—'}</span>
            </Flash>
            <span className="text-[7px] font-bold uppercase tracking-wide text-arena-400 dark:text-arena-200 mt-0.5">{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
// A compact labelled info cell (label + value + optional sub-line).
function InfoCell({ label, value, sub, icon, accent }: { label: string; value: string; sub?: string; icon?: string; accent?: string }) {
  return (
    <div className="flex-1 min-w-0 rounded-lg bg-white dark:bg-arena-900/50 border border-arena-100 dark:border-white/5 px-2.5 py-1.5">
      <div className="flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest text-arena-400 dark:text-arena-200 mb-0.5">{icon && <Icon name={icon} size={10} />}{label}</div>
      <div className={`font-black text-sm leading-tight truncate ${accent || 'text-arena-900 dark:text-white'}`}>{value}</div>
      {sub && <div className="text-[9px] font-medium text-arena-500 dark:text-arena-400 truncate">{sub}</div>}
    </div>
  );
}

// Colour-coded role chip for the squad list.
function RoleChip({ role, keeper }: { role: string; keeper: boolean }) {
  const r = keeper ? 'Wicketkeeper' : role;
  const cls = /keeper/i.test(r) ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'
    : /all/i.test(r) ? 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400'
    : /bowl/i.test(r) ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'
    : /bat/i.test(r) ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400'
    : 'bg-arena-100 text-arena-600 dark:bg-arena-700 dark:text-arena-200';
  const abbr = /keeper/i.test(r) ? 'WK' : /all/i.test(r) ? 'AR' : /bowl/i.test(r) ? 'BWL' : /bat/i.test(r) ? 'BAT' : '—';
  return <span className={`shrink-0 inline-flex items-center justify-center rounded px-1.5 py-0.5 text-[8px] font-black tracking-wider ${cls}`}>{abbr}</span>;
}

// Generic collapsible card for secondary sections (squads, match notes).
function Collapsible({ title, icon, count, children, defaultOpen = false }: { title: string; icon: string; count?: number; children: any; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white dark:bg-arena-800 border border-arena-200 dark:border-white/5 rounded-2xl overflow-hidden shadow-sm">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-arena-50 dark:hover:bg-arena-900/30 transition">
        <span className="flex items-center gap-2 font-black text-sm text-arena-900 dark:text-white">
          <Icon name={icon} size={16} className="text-brand-500" />{title}
          {count != null && <span className="text-[10px] font-bold text-arena-400">({count})</span>}
        </span>
        <Icon name={open ? 'expand_less' : 'expand_more'} size={20} className="text-arena-400" />
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-arena-100 dark:border-white/5">{children}</div>}
    </div>
  );
}

// Hand-drawn SVG badges (owner-requested, no icon font): the BATTING team wears a
// cricket bat on its flag, the BOWLING team a red seamed ball — same spot as the
// winner trophy, one size bigger so the roles read at a glance.
// Raw hand-drawn SVGs (shared): used inside the flag badges (with circle) and
// bare on the player cards (no circle) to mark the on-strike batsman / current
// bowler without eating name space.
function BatSvg({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <defs>
        <linearGradient id="willowFace" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#f2ddb0" />
          <stop offset="55%" stopColor="#e6c286" />
          <stop offset="100%" stopColor="#c9985a" />
        </linearGradient>
      </defs>
      <g transform="rotate(40 12 12)">
        <rect x="11" y="1.2" width="2" height="6.4" rx="1" fill="#4a2c15" />
        <line x1="11" y1="2.6" x2="13" y2="2.6" stroke="#7a4a24" strokeWidth="0.7" />
        <line x1="11" y1="4.2" x2="13" y2="4.2" stroke="#7a4a24" strokeWidth="0.7" />
        <line x1="11" y1="5.8" x2="13" y2="5.8" stroke="#7a4a24" strokeWidth="0.7" />
        <path d="M9 9.2 Q9 7.3 10.7 7.1 L13.3 7.1 Q15 7.3 15 9.2 L15 18.6 Q15 21.4 12 21.4 Q9 21.4 9 18.6 Z"
              fill="url(#willowFace)" stroke="#8a5a2e" strokeWidth="0.9" strokeLinejoin="round" />
        <path d="M10.5 7.4 L12 10.6 L13.5 7.4" fill="none" stroke="#a97e3f" strokeWidth="0.7" strokeLinejoin="round" />
        <line x1="12" y1="11.4" x2="12" y2="19.8" stroke="#c9a15e" strokeWidth="0.55" />
        <line x1="10.5" y1="11.2" x2="10.5" y2="19" stroke="#d8b678" strokeWidth="0.45" />
        <line x1="13.5" y1="11.2" x2="13.5" y2="19" stroke="#b88b48" strokeWidth="0.45" />
      </g>
    </svg>
  );
}
function BallSvg({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <defs>
        <radialGradient id="cricketLeather" cx="35%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#e6483d" />
          <stop offset="55%" stopColor="#c62b1f" />
          <stop offset="100%" stopColor="#8f150c" />
        </radialGradient>
      </defs>
      <circle cx="12" cy="12" r="9" fill="url(#cricketLeather)" stroke="#6f0f08" strokeWidth="0.8" />
      <ellipse cx="9" cy="8" rx="3.4" ry="2.2" fill="#ffffff" opacity="0.22" transform="rotate(-25 9 8)" />
      <path d="M10.9 3.3 C 9.9 8 9.9 16 10.9 20.7" fill="none" stroke="#f3e3c3" strokeWidth="0.85" />
      <path d="M13.1 3.3 C 14.1 8 14.1 16 13.1 20.7" fill="none" stroke="#f3e3c3" strokeWidth="0.85" />
      <line x1="10.5" y1="5.4"  x2="13.5" y2="6.4"  stroke="#f3e3c3" strokeWidth="0.6" />
      <line x1="10.4" y1="8.2"  x2="13.6" y2="7.2"  stroke="#f3e3c3" strokeWidth="0.6" />
      <line x1="10.3" y1="10.6" x2="13.7" y2="11.6" stroke="#f3e3c3" strokeWidth="0.6" />
      <line x1="10.3" y1="13.4" x2="13.7" y2="12.4" stroke="#f3e3c3" strokeWidth="0.6" />
      <line x1="10.4" y1="15.8" x2="13.6" y2="16.8" stroke="#f3e3c3" strokeWidth="0.6" />
      <line x1="10.5" y1="18.6" x2="13.5" y2="17.6" stroke="#f3e3c3" strokeWidth="0.6" />
    </svg>
  );
}
function BatBadge({ right = false }: { right?: boolean }) {
  return (
    <span className={`absolute -top-2 -right-2 w-5 h-5 rounded-full border shadow-md flex items-center justify-center ${right
      ? 'bg-emerald-500 border-emerald-200 dark:border-emerald-700'
      : 'bg-blue-500 border-blue-200 dark:border-blue-700'}`}>
      <BatSvg className="w-4 h-4" />
    </span>
  );
}
function BallBadge({ right = false }: { right?: boolean }) {
  return (
    <span className={`absolute -top-2 -right-2 w-5 h-5 rounded-full border shadow-md flex items-center justify-center ${right
      ? 'bg-emerald-500 border-emerald-200 dark:border-emerald-700'
      : 'bg-blue-500 border-blue-200 dark:border-blue-700'}`}>
      <BallSvg className="w-3.5 h-3.5" />
    </span>
  );
}

// One team as a compact SIDE// One team as a compact SIDE (left vs right preserved): the flag sits INLINE
// with the full name (mirrored on the right side), and the innings pills ride the
// line below — two short lines instead of the old three-stack, same information.
function TeamSide({ side, matchMeta, live }: { side: 0 | 1; matchMeta: any; live: any }) {
  const right = side === 1;
  const img = side === 0 ? matchMeta?.team_a_img : matchMeta?.team_b_img;
  const name = (side === 0 ? matchMeta?.team_a : matchMeta?.team_b) || live?.teams?.[side]?.name || (side === 0 ? 'Team A' : 'Team B');
  const scores = live?.teams?.[side]?.scores || '';
  const isWinner = live?.finished && live?.winnerSide === side;
  const lvTeams = live?.teams || [];
  const battingSide = !live?.finished && live?.battingTeam
    ? lvTeams.findIndex((t: any) => t.name && String(live.battingTeam).startsWith(t.name))
    : -1;
  const isBatting = battingSide === side;
  const isBowling = battingSide >= 0 && battingSide !== side;
  return (
    <div className={`flex-1 min-w-0 flex flex-col gap-1 ${right ? 'items-end' : 'items-start'}`}>
      <div className={`flex items-center gap-1.5 min-w-0 ${right ? 'flex-row-reverse' : ''}`}>
        <div className="relative shrink-0">
          {img ? (
            <img src={img} alt={name} className={`w-7 h-7 object-cover rounded-full shadow-sm bg-white border ${isWinner ? 'ring-2 ring-yellow-400 border-yellow-400' : 'border-arena-100 dark:border-white/10'}`} />
          ) : (
            <div className={`w-7 h-7 rounded-full flex items-center justify-center font-black text-xs ${isWinner ? 'bg-yellow-100 text-yellow-700 ring-2 ring-yellow-400' : 'bg-arena-100 dark:bg-arena-700 text-arena-400'}`}>{name[0]}</div>
          )}
          {isWinner && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-yellow-400 border border-white dark:border-arena-800 flex items-center justify-center shadow-md">
              <Icon name="emoji_events" size={9} className="text-black" />
            </span>
          )}
          {isBatting && <BatBadge right={right} />}
          {isBowling && <BallBadge right={right} />}
        </div>
        <span className={`font-black text-[13px] leading-tight min-w-0 ${right ? 'text-right' : ''} ${isWinner ? 'text-yellow-600 dark:text-yellow-400' : 'text-arena-900 dark:text-white'}`}>{name}</span>
      </div>
      {scores ? (
        <div className={`flex flex-wrap items-center gap-1 ${right ? 'justify-end' : 'justify-start'}`}>
          {parseInnings(scores).map((inn: any, i: number) => (
            <Flash key={i} value={`${inn.rw}|${inn.ov}`} rounded="rounded-md">
            <div className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 ${inn.current ? 'bg-brand-50 dark:bg-brand-500/10 ring-1 ring-brand-200 dark:ring-brand-500/30' : 'bg-arena-50 dark:bg-arena-900/40'}`}>
              <span className={`font-mono font-black text-sm leading-none ${inn.current ? 'text-brand-700 dark:text-brand-300' : 'text-arena-900 dark:text-white'}`}>{inn.rw}</span>
              {inn.ov && <span className="text-[8px] font-bold text-arena-400 dark:text-arena-200 uppercase tracking-wide">{inn.ov}ov</span>}
            </div>
            </Flash>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// One labelled stat cell (value on top, tiny caption below) — used to build the
// per-player stat strips so figures read as DATA, not a run-on sentence.
function Stat({ label, value, accent }: { label: string; value: any; accent?: boolean }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-1">
      <span className={`font-mono font-black text-base leading-none ${accent ? 'text-brand-600 dark:text-brand-400' : 'text-arena-900 dark:text-white'}`}>{value}</span>
      <span className="text-[8px] font-bold uppercase tracking-wider text-arena-400 mt-1 whitespace-nowrap">{label}</span>
    </div>
  );
}

// A batsman / bowler row: name header + a divided strip of stat cells.
function PlayerStatRow({ name, onStrike, flag, stats }: { name: string; onStrike?: boolean; flag?: string | null; stats: { label: string; value: any; accent?: boolean }[] }) {
  return (
    <div className={`rounded-xl p-3 ${onStrike ? 'bg-brand-50 dark:bg-brand-500/10 ring-1 ring-brand-200 dark:ring-brand-500/30' : 'bg-arena-50 dark:bg-arena-900/40'}`}>
      <div className="flex items-center gap-1.5 mb-2">
        {flag && <img src={flag} alt="" className="w-5 h-5 rounded-full object-cover bg-white border border-arena-100 dark:border-white/10 shrink-0" />}
        <span className="font-bold text-sm text-arena-900 dark:text-white">{name}</span>
        {onStrike && (
          <span className="ml-auto inline-flex items-center gap-1 text-[8px] font-black uppercase tracking-wider text-brand-600 dark:text-brand-400">
            <Icon name="sports_cricket" size={11} /> On strike
          </span>
        )}
      </div>
      <div className="flex items-stretch divide-x divide-arena-200/70 dark:divide-white/10">
        {stats.map((s, i) => <Stat key={i} label={s.label} value={s.value} accent={s.accent} />)}
      </div>
    </div>
  );
}

export default function MatchPage() {
  const { matchId } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState<any>(null);
  const [matchMeta, setMatchMeta] = useState<any>(null);
  const [selection, setSelection] = useState<BetSelection | null>(null);
  const [betStatus, setBetStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [betMessage, setBetMessage] = useState('');
  // Live wallet balance for the top-strip chip (2026-08-06 UX sweep — the
  // player needs to see what they can afford without leaving the match).
  const { balance: walletBalance } = useWallet();
  const [showInfo, setShowInfo] = useState(false);   // compact-UI: match-info expander
  const [tvOpen, setTvOpen] = useState(false);        // TV opens from the icon beside LIVE
  // Live position: per market → per runner → my net IF that runner wins.
  const [positions, setPositions] = useState<Record<string, Record<string, number>>>({});
  const loadPositions = async () => {
    try {
      const d = await api.get<any>(`/cricket/my-positions?matchId=${matchId}`);
      setPositions(d.positions || {});
    } catch { /* non-fatal */ }
  };
  useEffect(() => {
    void loadPositions();
    const t = setInterval(loadPositions, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  // The player's REAL bet bounds, straight from the engine (incl. any personal cap
  // their agent set). Fetched once — they don't move mid-session. Before this the
  // slip guessed 100 / 200,000 and was wrong in both directions, and a personal cap
  // was invisible until a bet got rejected. Non-fatal on failure: the slip falls
  // back to the SERVER defaults, so it stays honest either way.
  const [limits, setLimits] = useState<CricketLimits | null>(null);
  useEffect(() => {
    // C2 (2026-08-06): pass the match's real sport so a football/tennis slip shows THAT
    // sport's limits, not cricket's. matchMeta is null at mount (populated by a separate
    // effect), so the deps array below re-fires this once sport resolves — a bare append
    // alone would always send undefined and default to cricket. Money enforcement was
    // already correct server-side; this only fixes the SHOWN bounds.
    api.get<any>(`/cricket/my-limits?sport=${matchMeta?.sport || 'cricket'}`)
      .then((d) => setLimits({
        min: Number(d.min), max: Number(d.max),
        perMarket: d.perMarket || {},
        maxMatchExposure: d.maxMatchExposure ?? null,
        personal: !!d.personal,
        // The Exchange commission the server deducts from a winner's profit at
        // settlement. It rides on /my-limits because that is the one payload the
        // slip already has — see the note beside commissionPct in playerController's
        // myLimits. Null (endpoint older than this field, or the fetch failed) makes
        // the slip say nothing rather than guess a rate.
        commissionPct: d.commissionPct != null ? Number(d.commissionPct) : null,
      }))
      .catch(() => { /* non-fatal */ });
  }, [matchMeta?.sport]);

  useEffect(() => {
    // Resolve this exact match regardless of sport/status (?matchId= bypasses the
    // lobby's sport/status scoping, so football/tennis match pages open too).
    api.get<any>(`/cricket/matches?matchId=${encodeURIComponent(matchId as string)}`).then((res) => {
      if (res.matches) {
        const found = res.matches.find((m: any) => m.id === matchId);
        if (found) setMatchMeta(found);
      }
    }).catch(console.error);
  }, [matchId]);

  useEffect(() => {
    const poll = async () => {
      try {
        const res = await api.get<{ success: boolean; state: any }>('/cricket/state');
        if (res.state) {
          const ms = res.state[matchId as string] || (res.state.matchId === matchId ? res.state : null);
          if (ms) setState(ms);
        }
      } catch (err) { console.error(err); }
    };
    poll();
    const timer = setInterval(poll, 2000);
    return () => clearInterval(timer);
  }, [matchId]);

  const live = state?.live;
  const finished = !!live?.finished;
  // "Over" = the match has finished (live snapshot) OR the lobby meta says completed.
  // Over matches show NO odds and accept NO bets — enforced here in the UI and also
  // server-side (/state excludes completed matches; their markets are suspended).
  const over = matchMeta?.status === 'completed' || finished;

  if (!state) {
    // Reached only by a direct URL to a finished match (the lobby blocks the click).
    if (over) {
      return (
        <div className="max-w-3xl mx-auto pt-24 px-4 flex flex-col items-center text-center gap-4">
          <div className="w-16 h-16 rounded-full bg-arena-100 dark:bg-arena-800 flex items-center justify-center"><Icon name="lock_clock" size={32} className="text-arena-400" /></div>
          <h2 className="font-black text-xl text-arena-900 dark:text-white">This match is over</h2>
          <p className="text-sm font-medium text-arena-500 max-w-xs">{matchMeta?.match_result || 'The match has finished. Betting is closed.'}</p>
          <button onClick={() => nav(-1)} className="mt-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold text-sm">Back to Matches</button>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="animate-pulse text-arena-400">Loading match…</div>
      </div>
    );
  }

  // Which side (0=left/team A, 1=right/team B) is batting — used to place each team's
  // players on the SAME side as their flag above, with the correct Batting/Bowling label.
  const lvTeams = live?.teams || [];
  const teamFlags = [matchMeta?.team_a_img || null, matchMeta?.team_b_img || null];
  const teamNames = [matchMeta?.team_a || lvTeams[0]?.name || 'Team A', matchMeta?.team_b || lvTeams[1]?.name || 'Team B'];
  const teamShort = [lvTeams[0]?.short || matchMeta?.team_a || 'A', lvTeams[1]?.short || matchMeta?.team_b || 'B'];
  const battingSide = live?.battingTeam ? lvTeams.findIndex((t: any) => t.name && String(live.battingTeam).startsWith(t.name)) : -1;

  // Break detection (stumps / lunch / tea / innings break / rain): live but not
  // being PLAYED right now — shown as a clear banner so "LIVE but frozen" isn't
  // read as a bug.
  const idleTxt = !finished && live ? `${live.gameState || ''} | ${live.statusStr || ''} | ${live.statusNote || ''}` : '';
  const idleHit = idleTxt.match(/(stumps|end of day|lunch|tea break|\btea\b|innings break|rain|delay|wet outfield|no play|bad light|match to resume)/i);
  const isIdle = !!idleHit;
  const isStumps = isIdle && /stumps|end of day/i.test(idleTxt);

  return (
    <div className="space-y-3 pb-32 pt-3 px-1 sm:px-2 max-w-3xl mx-auto">

      {/* Top strip: back + wallet chip + rules (always) · bet-mode toggle (while bettable) */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button onClick={() => nav(-1)} aria-label="Back to all matches"
            className="w-9 h-9 rounded-lg bg-white dark:bg-arena-800 border border-arena-200 dark:border-white/10 shadow-sm flex items-center justify-center text-arena-600 dark:text-arena-200 hover:bg-arena-50 dark:hover:bg-arena-700 active:scale-95 transition">
            <Icon name="arrow_back" size={18} />
          </button>
          <RulesButton />
          {/* Live wallet chip (2026-08-06 UX sweep) — shows what you can
              afford right here; tap to top up without leaving the match. */}
          <button onClick={() => nav('/wallet')} aria-label="Your balance — tap to add money"
            className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg
                       bg-white dark:bg-arena-800 border border-arena-200 dark:border-white/10 shadow-sm
                       text-arena-800 dark:text-white hover:bg-arena-50 dark:hover:bg-arena-700 active:scale-95 transition">
            <Icon name="account_balance_wallet" size={15} className="text-brand-600 dark:text-brand-400" filled />
            <span className="font-black tabular-nums text-[13px] leading-none">
              ₹{(walletBalance || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </span>
            <Icon name="add_circle" size={13} className="text-arena-400 dark:text-neon-200/50" />
          </button>
        </div>
        {/* L-8 (2026-08-07): the old Detailed/Simple toggle is gone. "Simple"
            mode's ONLY effect was hiding the Lay column, which silently removed
            the player's ability to bet AGAINST an outcome — a first-class
            counterparty choice. Every non-fancy market now always shows Back+Lay
            with plain-language captions, so no bet type is ever hidden. */}
      </div>

      {/* ── ONE unified match + scoreboard card ───────────────────────────── */}
      <div className="bg-white dark:bg-arena-800 border border-arena-200 dark:border-white/5 rounded-2xl shadow-sm overflow-hidden">

        {/* Title + status */}
        <div className="px-2 pt-2.5 flex justify-between items-start gap-2">
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-bold text-brand-600 dark:text-brand-400 tracking-wider uppercase">
              {matchMeta?.competition_title ? `${matchMeta.competition_title}${matchMeta.format_str ? ` · ${matchMeta.format_str}` : ''}` : 'Cricket Match'}
            </span>
            {matchMeta?.subtitle && <span className="text-[10px] text-arena-500 dark:text-arena-200 font-medium">{matchMeta.subtitle}</span>}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Live TV — small icon beside the status chip (opens the player below) */}
            {!over && matchMeta?.live_tv ? (
              <button onClick={() => setTvOpen(!tvOpen)} aria-label="Watch Live TV"
                className={`relative inline-flex items-center justify-center w-6 h-6 rounded-md transition active:scale-95 ${tvOpen ? 'bg-red-600 text-white' : 'bg-red-500/15 text-red-500 hover:bg-red-500/25'}`}>
                <Icon name="live_tv" size={14} />
                {!tvOpen && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
              </button>
            ) : null}
            {finished ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/20 text-[10px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-widest"><Icon name="check_circle" size={12} /> Completed</span>
            ) : matchMeta?.status === 'in-play' || state?.status === 'in-play' ? (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-red-500/20 text-[10px] font-bold text-red-600 dark:text-red-400"><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> LIVE</span>
            ) : matchMeta?.status === 'upcoming' ? (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-blue-500/20 text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest">Upcoming</span>
            ) : null}
          </div>
        </div>

        {/* TV player — integrated INSIDE the card, only while open */}
        {tvOpen && !over && matchMeta?.live_tv ? <LiveTv url={matchMeta.live_tv.url} onClose={() => setTvOpen(false)} /> : null}

        {/* Teams — LEFT vs RIGHT preserved, compact two-line sides, V/S centered */}
        <div className="px-2 py-2 flex items-center justify-between gap-2">
          <TeamSide side={0} matchMeta={matchMeta} live={live} />
          <div className="shrink-0 self-center px-1">
            {matchMeta?.status === 'upcoming' && matchMeta?.start_time && !finished
              ? <CountdownTimer dateStr={matchMeta.start_time} />
              : <span className="text-[12px] font-black vs-heartbeat leading-none">V/S</span>}
          </div>
          <TeamSide side={1} matchMeta={matchMeta} live={live} />
        </div>

        {/* Win probability bar — live only, and ONLY when the feed actually provides
            usable percentages (a data-less bar reads as a bug, so it never renders). */}
        {!finished && live?.winPct
          && Number.isFinite(Number(live.winPct.a)) && Number.isFinite(Number(live.winPct.b))
          && (Number(live.winPct.a) + Number(live.winPct.b) + (Number(live.winPct.draw) || 0)) > 0
          ? <WinProbBar winPct={live.winPct} teamA={teamShort[0]} teamB={teamShort[1]} /> : null}

        {/* Result banner — kept prominent right under the teams when the match is over.
            Enriched with win margin and Man of the Match / Series when available. */}
        {finished && live?.statusNote && (
          <div className="mx-1.5 mb-3 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 px-3 py-2.5">
            <div className="flex items-center gap-2">
              <Icon name="emoji_events" size={18} className="text-yellow-500 shrink-0" />
              <span className="font-black text-sm text-emerald-700 dark:text-emerald-400">{live.statusNote}</span>
            </div>
            {(live.winMargin || (live.manOfMatch || []).length || (live.manOfSeries || []).length) ? (
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 pl-7 text-[10px] font-bold">
                {live.winMargin ? <span className="text-emerald-600 dark:text-emerald-400">Margin: <b className="text-arena-800 dark:text-arena-100">{live.winMargin}</b></span> : null}
                {(live.manOfMatch || []).length ? <span className="inline-flex items-center gap-1 text-arena-500"><Icon name="star" size={11} className="text-yellow-500" />MoM: <b className="text-arena-800 dark:text-arena-100">{live.manOfMatch.join(', ')}</b></span> : null}
                {(live.manOfSeries || []).length ? <span className="inline-flex items-center gap-1 text-arena-500"><Icon name="military_tech" size={11} className="text-yellow-500" />MoS: <b className="text-arena-800 dark:text-arena-100">{live.manOfSeries.join(', ')}</b></span> : null}
              </div>
            ) : null}
          </div>
        )}

        {/* ── COMPACT live details (V2): one inline situation line, then the deep
            detail as HORIZONTAL swipe strips — full information, minimal height. ── */}
        {!finished && (live?.statusNote || live?.liveScore) ? (
          <div className="mx-1.5 mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            {live?.statusNote && (
              <span className="inline-flex items-center gap-1.5 min-w-0">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse shrink-0" />
                <span className="text-[11px] font-bold text-arena-800 dark:text-arena-100">{live.statusNote}</span>
              </span>
            )}
            {live?.liveScore?.runrate ? <span className="text-[9px] font-bold uppercase tracking-wider text-arena-400 dark:text-arena-200 bg-arena-50 dark:bg-arena-900/40 rounded px-1.5 py-0.5">CRR <Flash value={live.liveScore.runrate} rounded="rounded" className="inline-flex"><b className="text-arena-800 dark:text-arena-200 font-mono px-0.5">{live.liveScore.runrate}</b></Flash></span> : null}
            {live?.liveScore?.required_runrate ? <span className="text-[9px] font-bold uppercase tracking-wider text-amber-500 bg-amber-50 dark:bg-amber-500/10 rounded px-1.5 py-0.5">RRR <Flash value={live.liveScore.required_runrate} rounded="rounded" className="inline-flex"><b className="font-mono px-0.5">{live.liveScore.required_runrate}</b></Flash></span> : null}
            {live?.liveScore?.target ? <span className="text-[9px] font-bold uppercase tracking-wider text-brand-500 bg-brand-50 dark:bg-brand-500/10 rounded px-1.5 py-0.5">TARGET <b className="font-mono">{live.liveScore.target}</b></span> : null}
          </div>
        ) : null}

        {/* Pulse grid (V3): every detail visible at once — NO horizontal scroll.
            Tight 2-col grid; last-wicket spans the full row. */}
        {!finished && ((live?.recentBalls || []).length || live?.partnership || live?.last5Overs || live?.lastWicket || live?.reviews) ? (
          <div className="mx-1.5 mb-2 grid grid-cols-2 gap-1">
            {live?.partnership ? (
              <MiniCard label="Partnership" icon="handshake" w="w-full">
                <div className="font-mono font-black text-sm leading-none text-arena-900 dark:text-white"><Flash value={`${live.partnership.runs}|${live.partnership.balls}`} rounded="rounded" className="inline-flex"><span className="px-0.5">{live.partnership.runs} <span className="text-[9px] text-arena-400 dark:text-arena-200 font-bold">({live.partnership.balls})</span></span></Flash></div>
                <div className="text-[8px] font-bold text-arena-400 dark:text-arena-200 mt-0.5 leading-tight">{(live.partnership.batsmen || []).map((x: any) => `${x.name} ${x.runs}`).join(' · ')}</div>
              </MiniCard>
            ) : null}
            {live?.last5Overs || (live?.reviews && (live.reviews.battingLeft != null || live.reviews.bowlingLeft != null)) ? (
              <MiniCard label={live?.last5Overs ? 'Last 5 overs' : 'DRS left'} icon="trending_up" w="w-full">
                {live?.last5Overs ? <div className="font-mono font-black text-sm leading-none text-arena-900 dark:text-white"><Flash value={String(live.last5Overs)} rounded="rounded" className="inline-flex"><span className="px-0.5">{String(live.last5Overs)}</span></Flash></div> : null}
                {live?.reviews && (live.reviews.battingLeft != null || live.reviews.bowlingLeft != null) ? (
                  <div className="text-[8px] font-bold text-arena-400 dark:text-arena-200 mt-0.5">DRS · Bat <Flash value={live.reviews.battingLeft} rounded="rounded" className="inline-flex"><b className="text-arena-800 dark:text-arena-200 font-mono px-0.5">{live.reviews.battingLeft ?? '—'}</b></Flash> · Bowl <Flash value={live.reviews.bowlingLeft} rounded="rounded" className="inline-flex"><b className="text-arena-800 dark:text-arena-200 font-mono px-0.5">{live.reviews.bowlingLeft ?? '—'}</b></Flash></div>
                ) : null}
              </MiniCard>
            ) : null}
            {live?.lastWicket ? (
              <div className="rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20 px-2 py-1.5 min-w-0 order-last">
                <div className="flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest text-red-400 dark:text-red-200 mb-0.5"><Icon name="sports_cricket" size={10} />Last wkt</div>
                <div className="text-[11px] font-black text-arena-900 dark:text-white leading-tight">
                  {live.lastWicket.name}{' '}
                  <Flash value={`${live.lastWicket.runs}|${live.lastWicket.balls}|${live.lastWicket.scoreAt}`} rounded="rounded" className="inline-flex">
                    <span className="px-0.5">{live.lastWicket.runs}{live.lastWicket.balls != null ? ` (${live.lastWicket.balls})` : ''}{live.lastWicket.scoreAt ? <span className="font-bold text-arena-400 dark:text-arena-200"> · at {live.lastWicket.scoreAt}</span> : null}</span>
                  </Flash>
                </div>
                {live.lastWicket.howOut ? <div className="text-[8px] font-medium text-red-400 dark:text-red-200 leading-tight">{live.lastWicket.howOut}</div> : null}
              </div>
            ) : null}
            {(live?.recentBalls || []).length ? (
              <div className="rounded-xl bg-arena-50 dark:bg-arena-900/40 border border-arena-100 dark:border-white/5 px-2 py-1.5">
                <div className="text-[8px] font-bold uppercase tracking-widest text-arena-400 dark:text-arena-200 mb-1">Last 6</div>
                <div className="flex items-center gap-1">
                  {(() => {
                    const sig = (live.recentBalls || []).map((x: any) => x.label).join(',');
                    return live.recentBalls.map((b: any, i: number) => {
                      const latest = i === live.recentBalls.length - 1;
                      const palette: Record<string, string> = {
                        dot: 'bg-arena-200 text-arena-600 dark:bg-arena-700 dark:text-arena-200',
                        run: 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300',
                        four: 'bg-blue-500 text-white',
                        six: 'bg-purple-600 text-white',
                        wicket: 'bg-red-500 text-white',
                      };
                      const chip = (
                        <span className={`inline-flex items-center justify-center rounded-full font-black font-mono leading-none ${latest ? 'w-[22px] h-[22px] text-[10px] ring-2 ring-brand-500' : 'w-[18px] h-[18px] text-[9px] opacity-75'} ${palette[b.kind] || palette.run}`}>
                          {b.label}
                        </span>
                      );
                      // only the NEWEST delivery flashes when a ball is added
                      return latest
                        ? <Flash key={i} value={sig} rounded="rounded-full" className="inline-flex">{chip}</Flash>
                        : <span key={i} className="inline-flex">{chip}</span>;
                    });
                  })()}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Players grid (V3): columns follow TEAM SIDE — the LEFT team's players
            always ride the LEFT column and the RIGHT team's the RIGHT column, no
            matter who is batting. Within each column the ACTIVE player (on strike /
            bowling) sits on top with the pulsing badge. */}
        {!finished && ((live?.batsmen || []).length || (live?.bowlers || []).length) ? (
          (() => {
            const bats = [...(live?.batsmen || [])].sort((a: any, b: any) => (b.onStrike ? 1 : 0) - (a.onStrike ? 1 : 0));
            const bowls = [...(live?.bowlers || [])].sort((a: any, b: any) => (b.bowling ? 1 : 0) - (a.bowling ? 1 : 0));
            // side 0 = left team, side 1 = right team; batting side owns the batsmen.
            const leftIsBatting = battingSide !== 1;            // unknown → default bats left
            const leftCol = leftIsBatting ? bats : bowls;
            const rightCol = leftIsBatting ? bowls : bats;
            const leftKind = leftIsBatting ? 'bat' : 'bowl';
            const rightKind = leftIsBatting ? 'bowl' : 'bat';
            const cards: any[] = [];
            const rows = Math.max(leftCol.length, rightCol.length);
            for (let i = 0; i < rows; i++) {
              if (leftCol[i]) cards.push({ kind: leftKind, p: leftCol[i], flag: teamFlags[0] });
              if (rightCol[i]) cards.push({ kind: rightKind, p: rightCol[i], flag: teamFlags[1] });
            }
            return (
              <div className="mx-1.5 mb-2 grid grid-cols-1 min-[360px]:grid-cols-2 gap-1">
                {cards.map((c, i) => c.kind === 'bat' ? (
                  <PlayerMini key={`bat-${i}`} full name={c.p.name} role="Bat" flag={c.flag}
                    active={c.p.onStrike ? 'bat' : null}
                    stats={[
                      { label: 'Runs', value: c.p.runs, accent: true },
                      { label: 'Balls', value: c.p.balls },
                      { label: '4s', value: c.p.fours },
                      { label: '6s', value: c.p.sixes },
                      { label: 'SR', value: c.p.sr },
                    ]} />
                ) : (
                  <PlayerMini key={`bowl-${i}`} full name={c.p.name} role="Bowl" flag={c.flag}
                    active={c.p.bowling ? 'bowl' : null}
                    stats={[
                      { label: 'Overs', value: c.p.overs },
                      { label: 'Mdns', value: c.p.maidens },
                      { label: 'Runs', value: c.p.runs },
                      { label: 'Wkts', value: c.p.wickets, accent: true },
                      { label: 'Econ', value: c.p.econ },
                    ]} />
                ))}
              </div>
            );
          })()
        ) : null}

        {/* Match info — one slim expander row instead of a permanent footer block. */}
        {((matchMeta && (matchMeta.venue_name || matchMeta.weather || matchMeta.pitch || matchMeta.toss)) || live?.day || live?.officials) ? (
          <div className="border-t border-arena-100 dark:border-white/5">
            <button onClick={() => setShowInfo(!showInfo)} className="w-full flex items-center justify-between px-2 py-1.5 text-[9px] font-bold uppercase tracking-widest text-arena-400 dark:text-arena-200 hover:text-arena-600 dark:hover:text-arena-100 transition">
              <span className="inline-flex items-center gap-1"><Icon name="info" size={11} />Match info{matchMeta?.toss?.text ? <span className="normal-case tracking-normal text-arena-500 dark:text-arena-200 font-medium ml-1">· {matchMeta.toss.text}</span> : null}</span>
              <Icon name={showInfo ? 'expand_less' : 'expand_more'} size={14} />
            </button>
            {showInfo && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-[10px] font-medium text-arena-600 dark:text-arena-400 px-2 pb-2.5">
                {matchMeta?.venue_name && <div className="flex items-center gap-1.5 min-w-0"><Icon name="location_on" size={12} className="opacity-70 shrink-0" /><span className="truncate">{matchMeta.venue_name}</span></div>}
                {live?.day ? <div className="flex items-center gap-1.5"><Icon name="today" size={12} className="opacity-70" /><span>Day {live.day}</span></div> : null}
                {matchMeta?.weather && typeof matchMeta.weather === 'object' && matchMeta.weather.weather && <div className="flex items-center gap-1.5"><Icon name="cloud" size={12} className="opacity-70" /><span>{matchMeta.weather.temp}°C, {matchMeta.weather.weather}</span></div>}
                {matchMeta?.pitch && typeof matchMeta.pitch === 'object' && matchMeta.pitch.pitch_condition && <div className="flex items-center gap-1.5"><Icon name="analytics" size={12} className="opacity-70" /><span>Pitch: {matchMeta.pitch.pitch_condition}</span></div>}
                {matchMeta?.toss && typeof matchMeta.toss === 'object' && matchMeta.toss.text && <div className="flex items-center gap-1.5 sm:col-span-2"><Icon name="monetization_on" size={12} className="opacity-70 text-yellow-500" /><span className="text-arena-700 dark:text-arena-200 font-bold">{matchMeta.toss.text}</span></div>}
                {live?.officials?.umpires ? <div className="flex items-start gap-1.5 sm:col-span-2 min-w-0"><Icon name="sports" size={12} className="opacity-70 shrink-0 mt-px" /><span className="truncate">Umpires: {live.officials.umpires}</span></div> : null}
                {live?.officials?.referee ? <div className="flex items-center gap-1.5 min-w-0"><Icon name="how_to_reg" size={12} className="opacity-70 shrink-0" /><span className="truncate">Referee: {live.officials.referee}</span></div> : null}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Markets — fully hidden once the match is over: no rates, no bet buttons. */}
      <div className="space-y-4">
        {over ? (
          <div className="bg-white dark:bg-arena-800 rounded-xl p-8 border border-arena-200 dark:border-white/5 flex flex-col items-center justify-center text-center space-y-2">
            <Icon name="lock" size={28} className="text-arena-300 dark:text-arena-600" />
            <div className="font-bold text-arena-600 dark:text-arena-200">Betting Closed</div>
            <div className="text-xs text-arena-400 max-w-xs">This match is over.{matchMeta?.match_result ? ` ${matchMeta.match_result}` : ''}</div>
          </div>
        ) : (() => {
          // A market only renders if it actually has CONTENT — at least one runner
          // with a live price, or (fancy) a temporarily-suspended session. Anything
          // the feed can't provide is simply not shown; no empty shells, no
          // "waiting" boxes per market. If NOTHING is bettable yet, one honest
          // placeholder for the whole area.
          const hasContent = (m: any) => Array.isArray(m.runners) && m.runners.some((r: any) =>
            (r.back?.[0]?.price && r.back[0].price !== '-') || (r.lay?.[0]?.price && r.lay[0].price !== '-') || r.suspended);
          const getOrder = (m: any) => {
            if (m.marketName === 'Match Odds' || m.marketType === 'match_odds') return 1;
            if (m.marketName === 'Bookmaker' || m.marketType === 'bookmaker') return 2;
            if (m.marketName === 'Session' || m.marketType === 'fancy') return 3;
            return 4;
          };
          const ms = (state.markets || []).filter(hasContent).sort((a: any, b: any) => getOrder(a) - getOrder(b));
          if (ms.length === 0) return (
            <div className="bg-white dark:bg-arena-800 rounded-xl p-8 border border-arena-200 dark:border-white/5 flex flex-col items-center justify-center text-center space-y-3">
              <Icon name="hourglass_empty" size={32} className="text-arena-300 dark:text-arena-600 animate-pulse" />
              <div className="font-bold text-arena-500 dark:text-arena-400">No odds for this match yet</div>
              <div className="text-xs text-arena-400 dark:text-arena-500 max-w-xs">The bookmaker hasn't priced this match. Rates and sessions appear here automatically the moment they're published — smaller matches are often priced late or not at all.</div>
            </div>
          );
          return ms.map((market: any) => {
          const isFancy = market.marketType === 'fancy' || market.marketName === 'Session';
          const displayTitle = market.marketName || (market.marketType === 'bookmaker' ? 'Bookmaker' : 'Match Odds');
          return (
            <div key={market.marketId} className="bg-white dark:bg-arena-800 rounded-xl overflow-hidden shadow-sm border border-arena-200 dark:border-white/5">
              <div className={`px-3 py-2 border-b border-arena-200 dark:border-white/5 flex justify-between items-center ${isFancy ? 'bg-blue-600 dark:bg-blue-800 text-white' : 'bg-arena-50 dark:bg-arena-700/50'}`}>
                <span className={`font-bold text-sm uppercase tracking-wider ${isFancy ? 'text-white' : 'text-arena-900 dark:text-white'}`}>{displayTitle}</span>
                {market.status && market.status !== 'open' && <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500 text-black uppercase">{market.status}</span>}
              </div>
              <div className={`flex px-3 py-1.5 border-b border-arena-200 dark:border-white/5 text-[11px] font-bold uppercase ${isFancy ? 'bg-blue-500 text-white/90 dark:bg-blue-700' : 'bg-white dark:bg-arena-800 text-arena-500 dark:text-white/40'}`}>
                <div className="flex-1 self-center">Runner</div>
                {/* Plain-language captions under the exchange jargon (2026-08-06
                    UX sweep) — a first-timer shouldn't need to know "back/lay". */}
                <div className="flex gap-1 justify-end w-32">
                  {isFancy ? (
                    <>
                      <div className="w-14 text-center leading-none">NO<span className="block text-[8.5px] font-semibold normal-case tracking-normal opacity-75 mt-0.5">under</span></div>
                      <div className="w-14 text-center leading-none">YES<span className="block text-[8.5px] font-semibold normal-case tracking-normal opacity-75 mt-0.5">over</span></div>
                    </>
                  ) : (
                    <>
                      <div className="w-14 text-center leading-none">Back<span className="block text-[8.5px] font-semibold normal-case tracking-normal opacity-75 mt-0.5">bet for</span></div>
                      <div className="w-14 text-center leading-none">Lay<span className="block text-[8.5px] font-semibold normal-case tracking-normal opacity-75 mt-0.5">bet against</span></div>
                    </>
                  )}
                </div>
              </div>
              <div className="divide-y divide-arena-100 dark:divide-white/5">
                {market.runners.map((runner: any, idx: number) => {
                  const backPrice = runner.back?.[0]?.price || '-';
                  const layPrice = runner.lay?.[0]?.price || '-';
                  const disabled = market.status && market.status !== 'open';
                  // marketType rides along so the slip can show THIS market's real
                  // cap (match odds / bookmaker / fancy each have their own).
                  // allowedModes + pricesByMode ride along so the slip can offer the
                  // House/Exchange choice and re-price instantly when it flips.
                  const pick = (price: string, type: 'back' | 'lay') => {
                    if (disabled || price === '-') return;
                    const pm = runner.priceByMode || {};
                    const modes: string[] = market.allowedModes?.length ? market.allowedModes : [market.defaultMode || 'house'];
                    const pricesByMode: Record<string, string> = {};
                    for (const md of modes) { const v = pm[md]?.[type]; if (v != null && v !== '') pricesByMode[md] = String(v); }
                    setSelection({ matchId: state.id, marketId: market.marketId, runnerName: runner.name, price, type, marketType: market.marketType, allowedModes: modes, mode: market.defaultMode || modes[0] || 'house', pricesByMode });
                  };
                  return (
                    <div key={idx} className="flex items-center px-3 py-2.5 hover:bg-arena-50 dark:hover:bg-white/5 transition">
                      <div className="flex-1 font-bold text-sm text-arena-900 dark:text-white pr-2">
                        {runner.name}
                        {/* Real fancy cap from /my-limits (was a hardcoded "MAX: 50K"
                            string that matched nothing the engine enforced). */}
                        {isFancy && limits?.perMarket?.fancy != null && (
                          <div className="text-[10px] font-medium text-arena-500">
                            MAX: {limits.perMarket.fancy.toLocaleString('en-IN')}
                          </div>
                        )}
                        {/* my live position on this runner: net if it wins */}
                        {!isFancy && (() => {
                          const p = positions[market.marketId]?.[String(runner.id)] ?? positions[market.marketId]?.[runner.id];
                          if (p == null || p === 0) return null;
                          return (
                            <Flash value={p} rounded="rounded" className="inline-block">
                            <div className={`text-[10px] font-mono font-black ${p < 0 ? 'text-red-500' : 'text-green-500'}`}>
                              {p < 0 ? `−₹${Math.abs(p).toLocaleString('en-IN')}` : `+₹${p.toLocaleString('en-IN')}`}
                            </div>
                            </Flash>
                          );
                        })()}
                      </div>
                      <div className="flex gap-1 justify-end w-32">
                        {isFancy ? (
                          runner.suspended ? (
                            /* Session temporarily suspended (ball in play / line moving) — visible, locked */
                            <div className="w-[116px] h-11 rounded bg-arena-100 dark:bg-arena-700/60 border border-arena-200 dark:border-white/10 flex items-center justify-center gap-1">
                              <Icon name="lock" size={12} className="text-arena-400" />
                              <span className="text-[9px] font-black uppercase tracking-widest text-arena-400">Suspended</span>
                            </div>
                          ) : (
                          <>
                            {/* NO (lay): runs line big, per-100 rate small */}
                            <Flash value={`${runner.lay?.[0]?.line}|${layPrice}`} rounded="rounded"><button disabled={disabled || layPrice === '-'} onClick={() => pick(layPrice, 'lay')} className="w-14 h-11 bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20 border border-red-100 dark:border-red-500/20 rounded active:scale-95 transition flex flex-col items-center justify-center disabled:opacity-30">
                              <span className="font-black text-sm leading-none text-red-600 dark:text-red-400">{runner.lay?.[0]?.line ?? '-'}</span>
                              <span className="text-[8px] font-bold text-red-400 mt-0.5">{layPrice !== '-' ? layPrice : ''}</span>
                            </button></Flash>
                            {/* YES (back): runs line big, per-100 rate small */}
                            <Flash value={`${runner.back?.[0]?.line}|${backPrice}`} rounded="rounded"><button disabled={disabled || backPrice === '-'} onClick={() => pick(backPrice, 'back')} className="w-14 h-11 bg-blue-50 hover:bg-blue-100 dark:bg-blue-500/10 dark:hover:bg-blue-500/20 border border-blue-100 dark:border-blue-500/20 rounded active:scale-95 transition flex flex-col items-center justify-center disabled:opacity-30">
                              <span className="font-black text-sm leading-none text-blue-700 dark:text-blue-400">{runner.back?.[0]?.line ?? '-'}</span>
                              <span className="text-[8px] font-bold text-blue-400 mt-0.5">{backPrice !== '-' ? backPrice : ''}</span>
                            </button></Flash>
                          </>
                          )
                        ) : (
                          <>
                            <Flash value={backPrice} rounded="rounded"><button disabled={disabled} onClick={() => pick(backPrice, 'back')} className="w-14 h-10 bg-blue-100 hover:bg-blue-200 dark:bg-blue-500/20 dark:hover:bg-blue-500/30 border border-blue-200 dark:border-blue-500/40 active:scale-95 transition rounded flex items-center justify-center disabled:opacity-30"><span className="font-bold text-sm text-blue-700 dark:text-blue-300">{backPrice}</span></button></Flash>
                            <Flash value={layPrice} rounded="rounded"><button disabled={disabled} onClick={() => pick(layPrice, 'lay')} className="w-14 h-10 bg-pink-100 hover:bg-pink-200 dark:bg-pink-500/20 dark:hover:bg-pink-500/30 border border-pink-200 dark:border-pink-500/40 active:scale-95 transition rounded flex items-center justify-center disabled:opacity-30"><span className="font-bold text-sm text-pink-700 dark:text-pink-300">{layPrice}</span></button></Flash>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
          });
        })()}
      </div>

      {/* ── Playing XI (collapsible, secondary) ── */}
      {live?.squads && (live.squads[0]?.length || live.squads[1]?.length) ? (
        <Collapsible title="Playing XI" icon="groups" count={(live.squads[0]?.length || 0) + (live.squads[1]?.length || 0)}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 pt-2">
            {[0, 1].map((side) => (
              live.squads[side]?.length ? (
                <div key={side}>
                  <div className="flex items-center gap-2 mb-2">
                    {teamFlags[side] && <img src={teamFlags[side]} alt="" className="w-5 h-5 rounded-full object-cover bg-white" />}
                    <span className="font-black text-xs text-arena-900 dark:text-white truncate">{teamNames[side]}</span>
                  </div>
                  <div className="space-y-1.5">
                    {live.squads[side].map((p: any, i: number) => (
                      <div key={i} className="flex items-center gap-2">
                        <RoleChip role={p.roleStr} keeper={p.keeper} />
                        <span className="font-bold text-xs text-arena-800 dark:text-arena-100 truncate">{p.name}{p.keeper ? <span className="ml-1 text-[9px] text-amber-500" title="Wicketkeeper">†</span> : null}</span>
                        {p.batStyle ? <span className="ml-auto text-[9px] font-medium text-arena-400 truncate hidden sm:block">{p.batStyle}</span> : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null
            ))}
          </div>
        </Collapsible>
      ) : null}

      {/* ── Match notes timeline (collapsible, secondary) ── */}
      {live?.matchNotes && live.matchNotes.length ? (
        <Collapsible title="Match Notes" icon="event_note" count={live.matchNotes.length}>
          <div className="pt-2 space-y-3">
            {live.matchNotes.map((grp: any, i: number) => {
              if (!Array.isArray(grp)) return null;
              const head = [grp[0], grp[1]].filter(Boolean).join(' · ');
              const notes = grp.slice(2).filter(Boolean);
              return (
                <div key={i}>
                  {head ? <div className="text-[10px] font-black uppercase tracking-wider text-brand-600 dark:text-brand-400">{head}</div> : null}
                  <ul className="mt-1 space-y-1">
                    {notes.map((n: any, j: number) => (
                      <li key={j} className="flex gap-1.5 text-[11px] text-arena-600 dark:text-arena-200"><span className="text-arena-300 dark:text-arena-600 shrink-0">•</span><span>{String(n)}</span></li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </Collapsible>
      ) : null}

      <BetSlip
        selection={selection}
        limits={limits}
        inPlay={state?.status === 'in-play' || matchMeta?.status === 'in-play'}
        onClose={() => setSelection(null)}
        onPlaceBet={async (stake, mode) => {
          if (!selection) return;
          const sel = selection;
          setBetStatus('loading');
          setSelection(null);
          try {
            // odds is server-authoritative (re-derived per mode); we send the
            // mode-matching price for the receipt + the chosen `mode` so the
            // server strikes House vs Exchange exactly as the player picked.
            const odds = sel.pricesByMode?.[mode] ?? sel.price;
            const data = await api.post<any>('/cricket/bet', { matchId, marketId: sel.marketId, selection: sel.runnerName, type: sel.type, odds, stake, mode });
            setBetMessage(data.message || 'Bet placed successfully');
            setBetStatus('success');
            if (data.newBalance != null) setWalletBalance(Number(data.newBalance));  // instant wallet re-sync
            void loadPositions();   // show the new book position immediately
          } catch (error: any) {
            setBetMessage(error.message || 'Bet Rejected');
            setBetStatus('error');
          } finally {
            setTimeout(() => setBetStatus('idle'), 3000);
          }
        }}
      />

      <AnimatePresence>
        {betStatus === 'success' || betStatus === 'error' ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.5, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.8, opacity: 0 }} transition={{ type: 'spring', damping: 15 }} className="bg-white dark:bg-arena-800 p-6 rounded-3xl shadow-2xl flex flex-col items-center max-w-xs w-full mx-4 border border-arena-100 dark:border-white/10">
              {betStatus === 'success' ? (
                <div className="w-20 h-20 bg-green-100 dark:bg-green-500/20 rounded-full flex items-center justify-center mb-4 text-green-500 shadow-inner"><Icon name="check_circle" size={48} /></div>
              ) : (
                <motion.div animate={{ x: [-10, 10, -10, 10, 0] }} transition={{ duration: 0.4 }} className="w-20 h-20 bg-red-100 dark:bg-red-500/20 rounded-full flex items-center justify-center mb-4 text-red-500 shadow-inner"><Icon name="cancel" size={48} /></motion.div>
              )}
              <h3 className={`text-xl font-black mb-1 ${betStatus === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{betStatus === 'success' ? 'Bet Accepted!' : 'Bet Rejected'}</h3>
              <p className="text-center font-bold text-sm text-arena-500 dark:text-arena-400">{betMessage}</p>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
